import { useState } from "react";
import { useAsyncEffect } from "tap/utils/hooks/useAsyncEffect";
import { tapApi } from "tap/api.js";
import { formatPmxCatalogsForDisplay } from "../utils/formatPmxCatalog";
import {
  getPmxCatalogUpdatesCache,
  setPmxCatalogUpdatesCache,
} from "../utils/pmxCatalogUpdatesCache";

const EMPTY_LICENSING_JOBS = {
  pending: [],
  updatedModified: [],
  updatedActive: [],
  duplicate: [],
  needsAttention: [],
  systemCreated: [],
};

function mapLicensingJobsResponse(jobsResponse) {
  return {
    pending: Array.isArray(jobsResponse["Pending"])
      ? jobsResponse["Pending"]
      : [],
    updatedModified: Array.isArray(jobsResponse["Updated/Modified"])
      ? jobsResponse["Updated/Modified"]
      : [],
    updatedActive: Array.isArray(jobsResponse["Updated and Active"])
      ? jobsResponse["Updated and Active"]
      : [],
    duplicate: Array.isArray(jobsResponse["Duplicate"])
      ? jobsResponse["Duplicate"]
      : [],
    needsAttention: Array.isArray(jobsResponse["Needs Attention"])
      ? jobsResponse["Needs Attention"]
      : [],
    systemCreated: Array.isArray(jobsResponse["System Created"])
      ? jobsResponse["System Created"]
      : [],
  };
}

/**
 * Loads Home dashboard widgets with independent fetches and PMX catalog cache.
 * @param {Object} options
 * @param {boolean} options.isTapUser - Whether to fetch TAP dashboard data
 * @returns {{ catalogs: Array, catalogsLoading: boolean, licensingJobsData: Object, jobsLoading: boolean }}
 */
export function useHomeDashboardData({ isTapUser }) {
  const [catalogs, setCatalogs] = useState(() => {
    if (!isTapUser) return [];
    const cached = getPmxCatalogUpdatesCache();
    return cached ? formatPmxCatalogsForDisplay(cached) : [];
  });
  const [catalogsLoading, setCatalogsLoading] = useState(() => {
    if (!isTapUser) return false;
    return !getPmxCatalogUpdatesCache();
  });
  const [licensingJobsData, setLicensingJobsData] = useState(EMPTY_LICENSING_JOBS);
  const [jobsLoading, setJobsLoading] = useState(isTapUser);

  useAsyncEffect(
    async ({ isMounted }) => {
      if (!isTapUser) {
        if (isMounted()) {
          setCatalogsLoading(false);
        }
        return;
      }

      const cached = getPmxCatalogUpdatesCache();
      if (cached?.length) {
        if (isMounted()) {
          setCatalogs(formatPmxCatalogsForDisplay(cached));
          setCatalogsLoading(false);
        }
      } else {
        if (isMounted()) {
          setCatalogsLoading(true);
        }
      }

      try {
        const data = await tapApi.pmxCatalogUpdates().catch(() => []);
        const rows = Array.isArray(data) ? data : [];
        setPmxCatalogUpdatesCache(rows);
        if (isMounted()) {
          setCatalogs(formatPmxCatalogsForDisplay(rows));
        }
      } finally {
        if (isMounted()) {
          setCatalogsLoading(false);
        }
      }
    },
    [isTapUser],
  );

  useAsyncEffect(
    async ({ isMounted }) => {
      if (!isTapUser) {
        if (isMounted()) {
          setJobsLoading(false);
        }
        return;
      }

      if (isMounted()) {
        setJobsLoading(true);
      }

      try {
        const jobsResponse =
          await tapApi.licensingJobStatusForUser().catch(() => ({}));
        if (isMounted()) {
          setLicensingJobsData(mapLicensingJobsResponse(jobsResponse));
        }
      } finally {
        if (isMounted()) {
          setJobsLoading(false);
        }
      }
    },
    [isTapUser],
  );

  return {
    catalogs,
    catalogsLoading,
    licensingJobsData,
    jobsLoading,
  };
}
